import { backendServices } from './backend.service';

const BASE = '/audit';

export type AuditLogCategory =
  | 'user'
  | 'asset'
  | 'compliance'
  | 'settings'
  | 'wallet'
  | 'report'
  | 'system';
export type AuditLogSeverity = 'info' | 'warning' | 'critical';

export interface AuditLog {
  id: string;
  action: string;
  category: AuditLogCategory;
  actor: string;
  details: string;
  ipAddress: string;
  timestamp: Date;
  severity: AuditLogSeverity;
}

export interface AuditLogListParams {
  page?: number;
  limit?: number;
  category?: string;
  severity?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface AuditLogListResponse {
  data: AuditLog[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AuditStats {
  total: number;
  today: number;
  warning: number;
  critical: number;
}

interface ApiResponse<T = unknown> {
  status: boolean;
  message?: string;
  data?: T;
  pagination?: AuditLogListResponse['pagination'];
}

function mapLog(raw: {
  _id: string;
  action: string;
  category: string;
  actor: string;
  details: string;
  ipAddress?: string;
  timestamp: string | Date;
  severity: string;
}): AuditLog {
  return {
    id: String(raw._id),
    action: raw.action,
    category: raw.category as AuditLogCategory,
    actor: raw.actor,
    details: raw.details,
    ipAddress: raw.ipAddress ?? '',
    timestamp: typeof raw.timestamp === 'string' ? new Date(raw.timestamp) : raw.timestamp,
    severity: raw.severity as AuditLogSeverity,
  };
}

export const auditApi = {
  async list(params: AuditLogListParams = {}): Promise<AuditLogListResponse> {
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.category && params.category !== 'all') q.set('category', params.category);
    if (params.severity && params.severity !== 'all') q.set('severity', params.severity);
    if (params.search?.trim()) q.set('search', params.search.trim());
    if (params.startDate) q.set('startDate', params.startDate);
    if (params.endDate) q.set('endDate', params.endDate);
    const query = q.toString();
    const url = query ? `${BASE}?${query}` : BASE;
    const res = await backendServices.get<ApiResponse<unknown[]>>(url);
    const data = res.data;
    if (!data?.status || !Array.isArray(data.data)) {
      throw new Error((data as { message?: string })?.message ?? 'Failed to load audit logs');
    }
    const list = data.data.map((item: Record<string, unknown>) =>
      mapLog(item as Parameters<typeof mapLog>[0])
    );
    return {
      data: list,
      pagination: data.pagination ?? {
        total: list.length,
        page: 1,
        limit: list.length,
        totalPages: 1,
      },
    };
  },

  async getStats(): Promise<AuditStats> {
    const res = await backendServices.get<ApiResponse<AuditStats>>(`${BASE}/stats`);
    const data = res.data;
    if (!data?.status || !data.data) {
      return { total: 0, today: 0, warning: 0, critical: 0 };
    }
    return data.data;
  },

  /** Export: fetch all matching logs (up to limit) for CSV download */
  async listForExport(params: Omit<AuditLogListParams, 'page'> & { limit?: number }) {
    const result = await this.list({ ...params, page: 1, limit: params.limit ?? 5000 });
    return result.data;
  },

  /** Create an audit log entry (e.g. for Activity Timeline) */
  async create(entry: {
    action: string;
    category: AuditLogCategory;
    actor: string;
    details: string;
    severity?: AuditLogSeverity;
    ipAddress?: string;
  }): Promise<void> {
    await backendServices.post<{ status: boolean }>('/audit', {
      action: entry.action,
      category: entry.category,
      actor: entry.actor,
      details: entry.details,
      severity: entry.severity ?? 'info',
      ipAddress: entry.ipAddress ?? '—',
    });
  },
};
